import * as Toast from "@radix-ui/react-toast";
import { CheckCircle2, AlertCircle, X } from "lucide-react";
import { createContext, useCallback, useContext, useState, ReactNode } from "react";
import clsx from "clsx";

type Kind = "ok" | "error" | "info";

/** Genau EINE Rückgängig-artige Aktion im Toast. Bewusst additiv und optional:
 *  jeder bestehende Aufruf bleibt unverändert.
 *
 *  `label` kommt FERTIG ÜBERSETZT vom Aufrufer (docs/i18n.md §3) — der Toast ist
 *  Infrastruktur und kennt keinen Katalog. */
export interface ToastAction {
  label: string;
  onAction: () => void;
}

interface ToastItem {
  id: number;
  kind: Kind;
  title: string;
  desc?: string;
  action?: ToastAction;
}

interface ToastCtx {
  push: (kind: Kind, title: string, desc?: string, action?: ToastAction) => void;
  ok: (title: string, desc?: string) => void;
  err: (title: string, desc?: string) => void;
  info: (title: string, desc?: string) => void;
}

// Ein Toast MIT Aktion steht länger: 4 s reichen zum Lesen, aber nicht zum
// Lesen UND Entscheiden UND Treffen. Outlook/Gmail liegen in derselben
// Größenordnung.
const DAUER_MS = 4000;
const DAUER_MIT_AKTION_MS = 9000;

const Ctx = createContext<ToastCtx | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const push = useCallback(
    (kind: Kind, title: string, desc?: string, action?: ToastAction) => {
      const id = Date.now() + Math.random();
      setItems((s) => [...s, { id, kind, title, desc, action }]);
    },
    []
  );
  const dismiss = (id: number) => setItems((s) => s.filter((i) => i.id !== id));

  const value: ToastCtx = {
    push,
    ok: (t, d) => push("ok", t, d),
    err: (t, d) => push("error", t, d),
    info: (t, d) => push("info", t, d),
  };

  return (
    <Ctx.Provider value={value}>
      <Toast.Provider swipeDirection="right" duration={DAUER_MS}>
        {children}
        {items.map((it) => (
          <Toast.Root
            key={it.id}
            // Stabiler Griff für die Walks. `[role="status"]` taugt dafür nicht:
            // Radix hängt neben der Karte eine zweite, unsichtbare Region mit
            // demselben role für Screenreader auf — je nach Reihenfolge las ein
            // Walk deren LEEREN Text und hielt eine korrekt angezeigte Meldung
            // für abwesend (gemessen 19.08.2026).
            data-toast={it.kind}
            duration={it.action ? DAUER_MIT_AKTION_MS : undefined}
            onOpenChange={(open) => !open && dismiss(it.id)}
            className={clsx(
              "rounded-lg border shadow-lg p-3 grid grid-cols-[auto_1fr_auto] gap-2 items-start",
              "bg-surface border-border",
              "data-[state=open]:animate-slide-up data-[state=closed]:animate-fade-in",
              it.kind === "ok" && "border-success/60",
              it.kind === "error" && "border-danger/60"
            )}
          >
            <span className="pt-0.5">
              {it.kind === "ok" ? (
                <CheckCircle2 className="w-5 h-5 text-success" />
              ) : it.kind === "error" ? (
                <AlertCircle className="w-5 h-5 text-danger" />
              ) : (
                <CheckCircle2 className="w-5 h-5 text-brand-600" />
              )}
            </span>
            <div className="min-w-0">
              <Toast.Title className="text-sm font-medium">{it.title}</Toast.Title>
              {it.desc && (
                <Toast.Description className="text-xs text-text-2 mt-0.5">
                  {it.desc}
                </Toast.Description>
              )}
              {it.action && (
                // `altText` ist bei Radix Pflicht: Screenreader-Nutzern, die den
                // Toast verpasst haben, sagt es, was hier zu tun wäre.
                <Toast.Action asChild altText={it.action.label}>
                  <button
                    type="button"
                    data-toast-action
                    onClick={() => {
                      it.action?.onAction();
                      dismiss(it.id);
                    }}
                    className="mt-1.5 rounded px-1.5 py-0.5 -ml-1.5 text-xs font-medium text-accent-600 transition-colors hover:bg-surface-2"
                  >
                    {it.action.label}
                  </button>
                </Toast.Action>
              )}
            </div>
            <Toast.Close asChild>
              <button className="text-text-3 hover:text-text">
                <X className="w-4 h-4" />
              </button>
            </Toast.Close>
          </Toast.Root>
        ))}
        <Toast.Viewport
          className="fixed right-4 flex flex-col gap-2 w-[360px] max-w-[90vw] z-50 outline-none"
          style={{ bottom: "max(env(safe-area-inset-bottom), 1rem)" }}
        />
      </Toast.Provider>
    </Ctx.Provider>
  );
}

export function useToast(): ToastCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error("useToast outside provider");
  return v;
}
